import React, {useEffect, useRef} from "react";
import {useAppDispatch, useAppSelector} from "../../../app/hooks";
import {
    addBotMessage,
    focusVehicle,
    getChatHistory,
    getWidgetSettings,
    horizontalOffsetSelector,
    initializeState,
    initializeUser,
    initialMessageSelector,
    isMobileSelector,
    isWidgetInitializedSelector,
    isWidgetOpenSelector,
    markWelcomeBubbleTimeoutAsElapsed,
    messagesSelector,
    openChatbot,
    possiblySendMessage,
    saveInitialBotMessage,
    showWidgetSelector,
    updateChatConvoStarters,
    updateFirstBotMessage,
    updateFullUrl,
    updateInitializationType,
    updateWelcomeBubbleConvoStarters,
    updateWelcomeBubbleFirstMessage,
    verticalOffsetSelector,
    websiteLocationSelector,
    widgetPositionSelector
} from "../../chatbotSlice";
import {LiveChat} from "../LiveChat";
import {sendChangeWidgetPositionEvent} from "../../chatbotUtil";
import {InitializationType, InputType} from "../../../api/generated";

export const MOBILE_WIDGET_HEIGHT = '100%'
export const MOBILE_WIDGET_WIDTH = '100%'

export const WIDGET_HEIGHT = '684px'
export const WIDGET_WIDTH = '410px'

export const BUBBLE_WITH_STARTERS_WIDTH = '370px'
export const BUBBLE_WITH_STARTERS_HEIGHT = '450px'

export const BUBBLE_WIDTH = '120px'
export const BUBBLE_HEIGHT = '120px'

export const App: React.FC = () => {

    const dispatch = useAppDispatch();

    const isMobile = useAppSelector(isMobileSelector);
    const isWidgetOpen = useAppSelector(isWidgetOpenSelector);
    const showWidget = useAppSelector(showWidgetSelector);
    const isWidgetInitialized = useAppSelector(isWidgetInitializedSelector);
    const websiteLocation = useAppSelector(websiteLocationSelector);
    const initialBotMessage = useAppSelector(initialMessageSelector);
    const messages = useAppSelector(messagesSelector);
    const widgetPosition = useAppSelector(widgetPositionSelector)
    const verticalOffsetPx = useAppSelector(verticalOffsetSelector);
    const horizontalOffsetPx = useAppSelector(horizontalOffsetSelector);

    const isFirstMessageSavedRef = useRef<boolean>(!!messages.length);
    const isWidgetInitializedRef = useRef<boolean>(isWidgetInitialized);

    useEffect(() => {
        window.addEventListener('message', handleMessage)
        initializeWidget()

        return () => {
            window.removeEventListener('message', handleMessage)
        }

    }, [])

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            dispatch(markWelcomeBubbleTimeoutAsElapsed());
        }, 5000);

        return () => clearTimeout(timeoutId);
    }, [dispatch])

    const handleMessage = async (event: MessageEvent) => {
        if (event.data.type === 'initialization') {
            dispatch(initializeState({
                ...event.data.storage,
                botId: event.data.botId,
                profile: event.data.profile,
                platformBaseURL: event.data.platformBaseURL,
                convoStartersTimeout: event.data.convoStartersTimeout,
                disableConvoStartersButtonsOnChatOnMobile: event.data.disableConvoStartersButtonsOnChatOnMobile,
                disableConvoStartersButtonsOnChatOnDesktop: event.data.disableConvoStartersButtonsOnChatOnDesktop,
                disableConvoStartersButtonsOnBubbleOnMobile: event.data.disableConvoStartersButtonsOnBubbleOnMobile,
                disableConvoStartersButtonsOnBubbleOnDesktop: event.data.disableConvoStartersButtonsOnBubbleOnDesktop,
                disableFirstMessageOnBubbleOnMobile: event.data.disableFirstMessageOnBubbleOnMobile,
                disableFirstMessageOnBubbleOnDesktop: event.data.disableFirstMessageOnBubbleOnDesktop,
                isMobile: event.data.isMobile,
                pageFullUrl: event.data.pageFullUrl
            }));

            await dispatch(getWidgetSettings(event.data.botId))

            if (!event.data.storage.chatId) {
                await dispatch(initializeUser())
            } else {
                await dispatch(getChatHistory())
            }

            isWidgetInitializedRef.current = true

            window.parent.postMessage(
                {
                    type: 'widget-init-complete',
                },
                '*',
            )
        } else if (isWidgetInitializedRef.current) {
            if (event.data.type === 'update-welcome-bubble-first-message') {
                dispatch(updateWelcomeBubbleFirstMessage(event.data.message));
            } else if (event.data.type === 'update-welcome-bubble-convo-starters') {
                dispatch(updateWelcomeBubbleConvoStarters(event.data.convoStarters));
            } else if (event.data.type === 'update-bot-focused-vehicle') {
                dispatch(focusVehicle({vin: event.data.VIN ?? event.data.vinNumber, vrm: event.data.VRM}));
            } else if (event.data.type === 'update-bot-first-message') {
                if (!isFirstMessageSavedRef.current) {
                    dispatch(updateFirstBotMessage(event.data.message));
                }
            } else if (event.data.type === 'update-chat-convo-starters') {
                dispatch(updateChatConvoStarters(event.data.convoStarters));
            } else if (event.data.type === 'open-widget') {
                if (!isFirstMessageSavedRef.current) {
                    isFirstMessageSavedRef.current = true;
                    await dispatch(saveInitialBotMessage())
                }

                dispatch(openChatbot());

                const isValidInitializationType = (value: any): value is InitializationType => {
                    return Object.values(InitializationType).includes(value);
                }

                const initializationType = event.data.initializationType;

                dispatch(
                    updateInitializationType(
                        isValidInitializationType(initializationType)
                            ? initializationType
                            : InitializationType.OnButtonClick
                    )
                );
            } else if (event.data.type === 'send-message-to-widget') {
                dispatch(possiblySendMessage({message: event.data.userMessage, inputType: InputType.Button}));
            } else if (event.data.type === 'add-bot-message') {
                dispatch(addBotMessage(event.data.botMessage));
            } else if (event.data.type === 'update-location') {
                dispatch(updateFullUrl(event.data.url));
            }
        }
    }

    const initializeWidget = () => {
        window.parent.postMessage(
            {
                type: 'widget-init-request',
            },
            '*',
        )
    }

    useEffect(() => {
        const verticalOffset = isMobile ? (isWidgetOpen ? 0 : verticalOffsetPx) : verticalOffsetPx
        const horizontalOffset = isMobile ? (isWidgetOpen ? 0 : horizontalOffsetPx) : horizontalOffsetPx
        sendChangeWidgetPositionEvent(widgetPosition, horizontalOffset, verticalOffset, websiteLocation)

        if (websiteLocation) {
            window.parent.postMessage(
                {
                    type: 'toggle-widget',
                    isWidgetOpen: isWidgetOpen
                },
                websiteLocation
            )
            resizeWidget()
        }
    }, [isWidgetOpen, websiteLocation, initialBotMessage])

    useEffect(() => {
        resizeWidget()
    }, [])

    const resizeWidget = () => {
        const widgetOffsetWidth = isWidgetOpen ? WIDGET_WIDTH : BUBBLE_WIDTH
        const widgetOffsetHeight = isWidgetOpen ? WIDGET_HEIGHT : BUBBLE_HEIGHT
        const mobileWidgetOffsetWidth = isWidgetOpen ? MOBILE_WIDGET_WIDTH : BUBBLE_WIDTH
        const mobileWidgetOffsetHeight = isWidgetOpen ? MOBILE_WIDGET_HEIGHT : BUBBLE_HEIGHT

        if (isMobile) {
            resizeWidgetEvent(mobileWidgetOffsetWidth, mobileWidgetOffsetHeight)
        } else {
            resizeWidgetEvent(widgetOffsetWidth, widgetOffsetHeight)
        }
    }

    const resizeWidgetEvent = (width: string, height: string) => {
        if (websiteLocation) {
            window.parent.postMessage(
                {
                    type: 'resize-widget',
                    width: width,
                    height: height,
                    isFullScreenWidget: false
                },
                websiteLocation
            )
        }
    }

    if (isWidgetInitialized && showWidget) {
        return <LiveChat resizeWidget={resizeWidgetEvent}/>
    } else {
        return <></>
    }
}
